/**
 * The locales the designer offers, with the flag each one shows.
 *
 * One list, because there are two consumers that must never drift: the Languages panel
 * (which adds locales to the project) and the Email Builder's per-template language
 * control. They previously carried the same 36 entries in two places, and only one of
 * them knew about the flags.
 *
 * `icon` is the basename under `public/images/flags/`; it is NOT derivable from the label
 * (English → unitedstates, Slovak → slovakian, Ukrainian → ukranian), which is exactly why
 * it is data rather than a rule.
 */

export interface IDesignerLanguageOption {
  label: string;
  /** The `xx_YY` locale code stored on the project and on a template's language rows. */
  value: string;
  icon: string;
}

export const DESIGNER_LANGUAGE_OPTIONS: IDesignerLanguageOption[] = [
  { label: "Albanian", value: "sq_AL", icon: "albanian" },
  { label: "Azerbaijani", value: "az_AZ", icon: "azerbaijani" },
  { label: "Bulgarian", value: "bg_BG", icon: "bulgarian" },
  { label: "Chinese(Simplified)", value: "zh_CN", icon: "chinese" },
  { label: "Catalan", value: "ca_ES", icon: "catalan" },
  { label: "Croatian", value: "hr_HR", icon: "croatian" },
  { label: "Czech", value: "cs_CZ", icon: "czech" },
  { label: "Danish", value: "da_DK", icon: "danish" },
  { label: "Dutch", value: "nl_NL", icon: "dutch" },
  { label: "English", value: "en_US", icon: "unitedstates" },
  { label: "English (United Kingdom)", value: "en_GB", icon: "uk" },
  { label: "Estonian", value: "et_EE", icon: "estonian" },
  { label: "Finnish", value: "fi_FI", icon: "finnish" },
  { label: "French", value: "fr_FR", icon: "french" },
  { label: "Georgian", value: "ka_GE", icon: "georgian" },
  { label: "German", value: "de_DE", icon: "german" },
  { label: "Greek", value: "el_GR", icon: "greek" },
  { label: "Hungarian", value: "hu_HU", icon: "hungarian" },
  { label: "Icelandic", value: "is_IS", icon: "icelandic" },
  { label: "Italian", value: "it_IT", icon: "italian" },
  { label: "Kazakh", value: "kk_KZ", icon: "kazakh" },
  { label: "Latvian", value: "lv_LV", icon: "latvian" },
  { label: "Lithuanian", value: "lt_LT", icon: "lithuanian" },
  { label: "Macedonian", value: "mk_MK", icon: "macedonian" },
  { label: "Norwegian Bokmål", value: "nb_NO", icon: "norwegian" },
  { label: "Polish", value: "pl_PL", icon: "polish" },
  { label: "Portuguese", value: "pt_PT", icon: "portuguese" },
  { label: "Romanian", value: "ro_RO", icon: "romanian" },
  { label: "Russian", value: "ru_RU", icon: "russian" },
  { label: "Serbian", value: "sr_RS", icon: "serbian" },
  { label: "Slovak", value: "sk_SK", icon: "slovakian" },
  { label: "Slovenian", value: "sl_SI", icon: "slovenian" },
  { label: "Spanish", value: "es_ES", icon: "spanish" },
  { label: "Swedish", value: "sv_SE", icon: "swedish" },
  { label: "Turkish", value: "tr_TR", icon: "turkish" },
  { label: "Ukrainian", value: "uk_UA", icon: "ukranian" }
];

/** The flag asset for a locale code, or undefined for anything not in the catalog. */
export const getLanguageFlagSrc = (localeCode: string | undefined): string | undefined => {
  if (!localeCode) return undefined;
  const option: IDesignerLanguageOption | undefined = DESIGNER_LANGUAGE_OPTIONS.find(
    (language: IDesignerLanguageOption): boolean => language.value === localeCode
  );
  if (!option) return undefined;
  return `/images/flags/${option.icon.toLowerCase()}.svg`;
};
